import React, { useState } from "react";
import { ActivityHistory, MasterDrawer, ViewOnlyNote } from "../Shared/master";
import { LOG_TYPES } from "../../helpers/activityLog";
import useCrudActions from "../../hooks/useCrudActions";
import Stars from "./Stars";
import {
  RATING_REPLY_ENABLED,
  REPLY_LENGTH,
  commentKinds,
  formatDateTime,
  isReplied,
  overallScore,
  ratedCategories,
  replyError,
  typeLabel,
} from "./ratingRules";
import { replyToRating } from "./ratingService";

const TABS = [
  { id: "details", label: "Rating & reply", icon: "fas fa-star" },
  { id: "history", label: "History", icon: "fas fa-history" },
];

/**
 * Slider of one rating: the scores and comment, the staff reply (Edit right) and the reply's history.
 * While replies are switched off (RATING_REPLY_ENABLED) only the rating shows, plus an old reply read-only.
 */
export default function RatingDrawer({ rating, canEdit, onChanged, onClose, replyEnabled = RATING_REPLY_ENABLED }) {
  const [tab, setTab] = useState("details");
  const [opened, setOpened] = useState({ details: true });
  const [reply, setReply] = useState(rating.replyMsg ?? "");
  const [error, setError] = useState("");
  const [historyKey, setHistoryKey] = useState(0);
  const { busy, save } = useCrudActions();
  const replied = isReplied(rating);
  const unchanged = reply.trim() === (rating.replyMsg ?? "").trim();
  const kinds = commentKinds(rating);

  const selectTab = (id) => {
    setTab(id);
    setOpened((prev) => ({ ...prev, [id]: true }));
  };

  const sendReply = async () => {
    const problem = replyError(reply);
    setError(problem);
    if (problem) return;
    await save(() => replyToRating({ id: rating.id, replyMsg: reply.trim() }), {
      onSuccess: async () => {
        setHistoryKey((k) => k + 1);
        await onChanged();
      },
    });
  };

  return (
    <MasterDrawer
      title={rating.placeName || typeLabel(rating)}
      subtitle={`${typeLabel(rating)}${rating.airportCode ? ` · ${rating.airportCode}` : ""} · Rating ${rating.id}`}
      size="medium"
      showSave={false}
      onClose={onClose}
    >
      {replyEnabled && (
        <div className="mst-range-tabs mst-range-tabs--sm mst-drawer-tabs" role="tablist" aria-label="Rating">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`mst-range-tab${tab === t.id ? " is-selected" : ""}`}
              onClick={() => selectTab(t.id)}
            >
              <i className={t.icon} aria-hidden="true" />
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      )}

      {tab === "details" && (
        <>
          <section className="rtg-score" aria-label="Scores">
            <div className="rtg-score__overall">
              <span className="rtg-score__number">{overallScore(rating) ? overallScore(rating).toFixed(1) : "–"}</span>
              <Stars score={overallScore(rating)} />
              <span className="rtg-score__label">Overall</span>
            </div>
            <ul className="rtg-score__list">
              {ratedCategories(rating).map((c) => (
                <li key={c.field}>
                  <span>{c.label}</span>
                  <Stars score={c.score} />
                </li>
              ))}
            </ul>
          </section>

          <dl className="mst-details">
            <dt>Crew</dt>
            <dd>
              {rating.crewName || "-"}
              {rating.employeeNumber ? ` (${rating.employeeNumber})` : ""}
              {rating.designation ? ` · ${rating.designation}` : ""}
            </dd>
            {rating.vehicleNo && (
              <>
                <dt>Vehicle</dt>
                <dd>{rating.vehicleNo}</dd>
              </>
            )}
            <dt>Stay / trip date</dt>
            <dd>{formatDateTime(rating.reservationDate) || "-"}</dd>
            <dt>Rated on</dt>
            <dd>{formatDateTime(rating.dateOfRating) || "-"}</dd>
            {rating.hasImage && (
              <>
                <dt>Photo</dt>
                <dd>Attached in the crew app</dd>
              </>
            )}
          </dl>

          <section className="rtg-comment" aria-label="Comment">
            <h4>
              <i className="fas fa-quote-left" aria-hidden="true" /> Comment
              {kinds.map((k) => (
                <span key={k} className={`rtg-kind is-${k.toLowerCase()}`}>
                  {k}
                </span>
              ))}
            </h4>
            <p className={rating.comments ? "" : "text-muted"}>{rating.comments || "No comment."}</p>
          </section>

          {replyEnabled ? (
            <section className="rtg-reply" aria-label="Staff reply">
              <h4>
                <i className="fas fa-reply" aria-hidden="true" /> Reply to the crew member
              </h4>
              {replied && (
                <p className="rtg-reply__meta">
                  Sent by {rating.replyBy || "-"}
                  {rating.replyAt ? ` · ${formatDateTime(rating.replyAt)}` : ""}
                </p>
              )}
              {canEdit ? (
                <>
                  <textarea
                    className="form-control"
                    rows={4}
                    aria-label="Reply"
                    placeholder="e.g. Thank you, we have shared this with the hotel."
                    value={reply}
                    disabled={busy}
                    onChange={(e) => {
                      setReply(e.target.value);
                      setError("");
                    }}
                  />
                  <div className="rtg-reply__foot">
                    {error ? (
                      <span className="rtg-reply__error" role="alert">
                        <i className="fas fa-exclamation-circle" aria-hidden="true" /> {error}
                      </span>
                    ) : (
                      <span className={`rtg-reply__count${reply.trim().length > REPLY_LENGTH ? " is-over" : ""}`}>
                        {reply.trim().length} / {REPLY_LENGTH} · the crew member sees it in the crew app
                      </span>
                    )}
                    <button type="button" className="btn btn-primary btn-sm" disabled={busy || unchanged} onClick={sendReply}>
                      <i className={`fas fa-${busy ? "spinner fa-spin" : "paper-plane"}`} aria-hidden="true" /> {replied ? "Update reply" : "Send reply"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className={replied ? "rtg-reply__text" : "text-muted"}>{replied ? rating.replyMsg : "No reply yet."}</p>
                  <ViewOnlyNote />
                </>
              )}
            </section>
          ) : (
            replied && (
              <section className="rtg-reply" aria-label="Staff reply">
                <h4>
                  <i className="fas fa-reply" aria-hidden="true" /> Staff reply
                </h4>
                <p className="rtg-reply__meta">
                  Sent by {rating.replyBy || "-"}
                  {rating.replyAt ? ` · ${formatDateTime(rating.replyAt)}` : ""}
                </p>
                <p className="rtg-reply__text">{rating.replyMsg}</p>
              </section>
            )
          )}
        </>
      )}
      {replyEnabled && opened.history && (
        <div hidden={tab !== "history"}>
          <ActivityHistory logType={LOG_TYPES.rating} columnValue1={String(rating.id)} reloadKey={historyKey} />
        </div>
      )}
    </MasterDrawer>
  );
}
